import { PAGE_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/shadcn/sidebar'
import { TooltipButton } from '@/components/ui/TooltipButton'
import clsx from 'clsx'
import { type ComponentType } from 'react'
import { NavLink, useMatch, useResolvedPath } from 'react-router'

type SidebarIconProps = {
  className?: string
}

export type FlatSidebarMenuItem = {
  title: string
  Icon: ComponentType<SidebarIconProps>
  path: string
  visible?: boolean
  end?: boolean
}

export function FlatSidebarMenu({
  items,
  sidebarOpen = true,
}: {
  items: FlatSidebarMenuItem[]
  sidebarOpen?: boolean
}) {
  return (
    <nav style={{ paddingTop: PAGE_HEADER_HEIGHT }} className="pr-2">
      <SidebarMenu>
        {items
          .filter((item) => item.visible ?? true)
          .map((item) => (
            <FlatSidebarMenuLink key={item.path} item={item} sidebarOpen={sidebarOpen} />
          ))}
      </SidebarMenu>
    </nav>
  )
}

function FlatSidebarMenuLink({ item, sidebarOpen }: { item: FlatSidebarMenuItem; sidebarOpen: boolean }) {
  const resolvedPath = useResolvedPath(item.path)
  const isActive = Boolean(
    useMatch({
      path: resolvedPath.pathname,
      end: item.end ?? true,
    })
  )

  return (
    <SidebarMenuItem className="mt-2">
      <TooltipButton tooltip={item.title} side="right" disabled={sidebarOpen}>
        <SidebarMenuButton asChild isActive={isActive} className="relative min-w-0">
          <NavLink to={item.path} end={item.end ?? true} aria-current={isActive ? 'page' : undefined}>
            <div
              className={clsx(
                'flex min-w-0 flex-1 items-center gap-x-2 overflow-hidden transition-[max-width] duration-300 ease-in-out',
                {
                  'max-w-[252px]': sidebarOpen,
                  'max-w-[36px]': !sidebarOpen,
                }
              )}
            >
              <div
                className={clsx('absolute left-1 top-[calc(50%-12px)] h-[24px] w-[4.5px] rounded-full bg-primary', {
                  hidden: !isActive,
                })}
              />
              <item.Icon className="size-6 shrink-0" />
              <span
                className={clsx('min-w-0 flex-1 truncate font-light transition-opacity duration-200', {
                  invisible: !sidebarOpen,
                  '!font-bold tracking-tighter text-foreground': isActive,
                })}
              >
                {item.title}
              </span>
            </div>
          </NavLink>
        </SidebarMenuButton>
      </TooltipButton>
    </SidebarMenuItem>
  )
}
